'use client'

import { Stack, Text } from '@mantine/core'
import type { Sku, SkuPriceOptionsResponse } from '@/types/interfaces/sku'
import { CompactVisitOptionRow } from '@/components/VisitOptions/CompactVisitOptionRow'
import { OfficeVisitOption } from '@/components/VisitOptions/OfficeVisitOption'
import {
    type CompactVisitType,
    getCompactMeta,
    getCompactVisitPricing,
} from '@/components/VisitOptions/visitOptionsHelpers'

interface InPersonFirstLayoutProps {
    sku: Sku
    prices: SkuPriceOptionsResponse | null
    /** Visit types other than `in-person-visit` to render under "Other ways to start". */
    secondaryVisitTypes: CompactVisitType[]
    onClick: () => void
    onOfficeVisitClick: (e: React.MouseEvent) => void
}

/**
 * Layout used when in-person care is enabled for the customer: features the
 * office visit as a prominent primary card and lists virtual options as a
 * compact "Other ways to start" section below. The primary slot is fixed
 * to `OfficeVisitOption` by design — no dispatch needed.
 */
export function InPersonFirstLayout({
    sku,
    prices,
    secondaryVisitTypes,
    onClick,
    onOfficeVisitClick,
}: InPersonFirstLayoutProps) {
    return (
        <Stack gap={20}>
            <div onClick={onOfficeVisitClick} style={{ cursor: 'pointer' }}>
                <OfficeVisitOption
                    sku={sku}
                    prices={prices}
                    subtitle="With a clinician near you"
                    responsive
                />
            </div>
            {secondaryVisitTypes.length > 0 ? (
                <OtherWaysToStart
                    types={secondaryVisitTypes}
                    prices={prices}
                    sku={sku}
                    onClick={onClick}
                />
            ) : null}
        </Stack>
    )
}

interface OtherWaysToStartProps {
    types: CompactVisitType[]
    prices: SkuPriceOptionsResponse | null
    sku: Sku
    onClick: () => void
}

function OtherWaysToStart({ types, prices, sku, onClick }: OtherWaysToStartProps) {
    return (
        <Stack gap={12}>
            <Stack gap={4}>
                <Text
                    c={'var(--Foreground-fg-primary, #1F1E1D)'}
                    fz={16}
                    fw={'var(--weight-text-bold, 600)'}
                    lh={'130%'}
                >
                    Other ways to start
                </Text>
                <Text
                    c="var(--gm-foreground-fg-secondary)"
                    fz={14}
                    fw={'var(--weight-text, 500)'}
                    lh={'130%'}
                >
                    A clinician can review virtually, but this service may still require an in-person visit.
                </Text>
            </Stack>
            <Stack gap={8}>
                {types.map((type) => {
                    const meta = getCompactMeta(type)
                    const { price, insuranceSubtext, isUpsellInsurance } = getCompactVisitPricing(prices, sku, type)
                    return (
                        <CompactVisitOptionRow
                            key={`compact-${type}`}
                            optionType={type}
                            icon={meta.icon}
                            title={meta.title}
                            price={price}
                            insuranceSubtext={insuranceSubtext}
                            isUpsellInsurance={isUpsellInsurance}
                            onClick={onClick}
                        />
                    )
                })}
            </Stack>
        </Stack>
    )
}
